import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import useDebounce from './hooks/useDebounce';
import EvacuationZoneQuery from './EvacuationZoneQuery';
import './EvacuationZoneSearch.css';

const EvacuationZoneLookup = () => {
    const language = document.getElementById('evac_zone_language')?.value || 'en';
    const [searching, setSearching] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [address, setAddress] = useState('');
    const [displayAddress, setDisplayAddress] = useState('');
    const debouncedSearchTerm = useDebounce(address, 400);
    const [suggestions, setSuggestions] = useState([]);
    const [results, setResults] = useState(null);
    const [evacuationZoneQuery, setEvacuationZoneQuery] = useState(null);    
    const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(null);

    useEffect(() => {        
        const initializeSearch = async () => {
            const handleSetResults = results => {
                handleSetSuggestions([]);
                setResults(results);
            };
            
            const evacZonesQuery = new EvacuationZoneQuery(handleSetSuggestions, handleSetResults, setErrorMessage, setSearching, language);
            await evacZonesQuery.initializeSearch(); 
            setEvacuationZoneQuery(evacZonesQuery);
        }
        initializeSearch();
    }, [language]);

    useEffect(() => {
        if (debouncedSearchTerm?.length > 3) {
            evacuationZoneQuery.suggestResults(debouncedSearchTerm);
        } else {
            handleSetSuggestions([]);
        }
    }, [debouncedSearchTerm, evacuationZoneQuery]);

    const handleSetSuggestions = newSuggestions => {
        setSelectedSuggestionIndex(null);
        setSuggestions(newSuggestions);
    };

    const handleAddressChange = event => {
        setDisplayAddress(event.target.value);
        setAddress(event.target.value);
    };

    const handleAddressSearch = async () => {
        setErrorMessage(null);
        setResults(null);
        handleSetSuggestions([]);
        setAddress('');

        if (selectedSuggestionIndex != null) {
            evacuationZoneQuery.searchAddress(suggestions[selectedSuggestionIndex].text);
            setDisplayAddress(suggestions[selectedSuggestionIndex].text);
        } else if (displayAddress) {
            evacuationZoneQuery.searchAddress(displayAddress);
        }
    };

    const handleInputKeypress = event => {
        if (event.which === 36 || event.keyCode === 36 || event.key === 'Home') {
            event.preventDefault();
            event.stopPropagation();
            if (suggestions?.length > 0) {
                setSelectedSuggestionIndex(0);
            }
        } else if (event.which === 35 || event.keyCode === 35 || event.key === 'End') { 
            event.preventDefault();
            event.stopPropagation();
            if (suggestions?.length > 0) {
                setSelectedSuggestionIndex(suggestions.length - 1);
            }
        } else if (event.which === 40 || event.keyCode === 40 || event.key === 'ArrowDown') {
            event.preventDefault();
            event.stopPropagation();    
            if (suggestions?.length > 0) {
                setSelectedSuggestionIndex((selectedSuggestionIndex == null) || (selectedSuggestionIndex + 1 === suggestions.length) ? 0 : selectedSuggestionIndex + 1);
            }
        } else if (event.which === 38 || event.keyCode === 38 || event.key === 'ArrowUp') {
            event.preventDefault();
            event.stopPropagation();
            if (suggestions?.length > 0) {
                setSelectedSuggestionIndex((selectedSuggestionIndex == null) || (selectedSuggestionIndex === 0) ? suggestions.length - 1 : selectedSuggestionIndex - 1);
            }
        } else if (event.which === 27 || event.keyCode === 27 || event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            setDisplayAddress('');
            setAddress('');
            setSelectedSuggestionIndex(null);
            handleSetSuggestions([]);
        } else if (event.which === 13 || event.keyCode === 13 || event.key === "Enter") {
            handleAddressSearch();
        }
    };

    const renderSuggestions = () => {
        if (suggestions && suggestions.length > 0) {
            return suggestions.map((suggestion, index) => {
                return (
                    <li
                        key={suggestion.key}
                        role="option"                        
                        aria-selected={(selectedSuggestionIndex != null && selectedSuggestionIndex === index) ? true : null}
                        className="evac-suggestion grve-custom-btn"
                        id={`suggestion_${index}`}
                        onClick={() => handleAddressSearch()}
                        onMouseOver={() => setSelectedSuggestionIndex(index)}
                    >
                        <span>{suggestion.text}</span>
                    </li>
                );
            });
        }
    };

    const renderResults = () => {
        if (results?.address) {
            return (
                <div>
                    <h4>{language === 'en' ? 'Search Results' : 'Resultados de la búsqueda'}</h4>
                    <p className='notranslate'><strong>{results.address}</strong></p>
                    <ul>
                        <li><strong>{language === 'en' ? 'Zone' : 'Zona'} <span className='notranslate'>{results.evacZone}</span></strong></li>
                        <li><strong>{language === 'en' ? 'Status' : 'Estado'}</strong>: {results.status}</li>
                        <li>
                            <strong>{results.summary}</strong>
                            <ul>
                                {renderZoneDescription()}
                            </ul>
                        </li>                                        
                    </ul>
                    {results.pdfMapUrl &&
                        <a 
                            href={results.pdfMapUrl}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {language === 'es' ? `Mapa Zona ${results.evacZone} (PDF)` : `Zone ${results.evacZone} Map (PDF)`}
                        </a>
                    }
                </div>
            );
        }
    };

    const renderZoneDescription = () => {
        return results.description?.map(line => {
            return <li key={line}>{line}</li>;
        });
    };

    return (
        <div className="evac-search-outer-container">
            <div className="evac-search-container">
                <div>
                    {evacuationZoneQuery !== null ?
                    <div className="evac-address-input-container">
                        <label htmlFor="address-input-field">{language === 'en' ? 'Enter your Address:' : 'Ingrese su dirección:'}</label>
                        <div 
                            className="evac-address-input"
                            aria-haspopup="listbox"
                            aria-owns="search-suggestions"
                            aria-expanded={suggestions?.length > 0}
                        >
                            <input 
                                id="address-input-field" 
                                type="search" 
                                className="evac-address-search"
                                placeholder={language === 'en' ? "Address" : 'dirección'}
                                value={displayAddress} 
                                onChange={handleAddressChange} 
                                onKeyDown={handleInputKeypress}
                                autoComplete="off"
                                aria-controls="search-suggestions"
                                aria-autocomplete="none"
                                aria-activedescendant={selectedSuggestionIndex != null ? `suggestion_${selectedSuggestionIndex}` : null}
                            />
                            <button 
                                type="button" 
                                className="grve-custom-btn" 
                                onClick={() => handleAddressSearch()}                            
                            >
                                {language === 'en' ? 'Search' : 'Buscar'}
                            </button>
                        </div>                        
                    </div>
                    : null}
                    <div className="evac-suggestion-container">
                        <div className="evac-split">                        
                            <ul 
                                className={`evac-search-suggestions${suggestions && suggestions.length > 0 ? ' active' : ''}`}
                                id="search-suggestions"
                                role="listbox"
                                aria-label={language === 'en' ? 'Address search suggestions' : 'Sugerencias de búsqueda de direcciones'}
                            >
                                {renderSuggestions()}
                            </ul>
                            <div className="evac-spacer"></div>
                        </div>
                    </div>
                </div>
            </div>
            {results || searching || errorMessage ? 
            <div 
                className="evac-search-results"
                aria-live="polite"
            >
                {searching ? 
                    <p>{(language === 'en' ? 'Searching...' : 'Buscando...')}</p>
                : null}
                {errorMessage ? 
                    <p>{errorMessage}</p>
                : null}
                {renderResults()}
            </div>
            : null}
        </div>
    );
};

ReactDOM.render(<EvacuationZoneLookup />, document.getElementById('evac-zone-root'));